import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { Alert, Box, Grid, Paper, Stack, Typography } from "@mui/material";
import type { Result } from "hooks/utils/material-data";
import type { CdcUserAttributes } from "models/cdcUser";
import { EuropeanCountry } from "pages/commons/constants";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import { type FC, type ReactNode, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { ColorSpecificationList } from "../colorSpecificationList/ColorSpecificationList";
import { DigitalSamples } from "../digitalSamples/DigitalSamples";
import { RestrictionsBanner } from "../restrictions/restrictions-banner";
import { AvailabilityInformation } from "./AvailabilityInformation";
import { ColorAndMaterialInformation } from "./ColorAndMaterialInformation";
import { CompetitorInformation } from "./CompetitorInformation";
import { CustomerInformation } from "./CustomerInformation";
import HazardClass from "./HazardClass";
import { OpticalMaterialData } from "./OpticalMaterialData";

export interface ColoredMaterialDetailsProps {
  material: ColoredMaterial;
  isInternal?: boolean;
  grade: Result | undefined;
  isInternalOnly: boolean;
  isRestrcited: boolean;
  isTrial: boolean;
  isInternalIndustry: boolean;
}

const Section = (props: {
  title: ReactNode | ReactNode[];
  children?: ReactNode | ReactNode[];
}) => {
  return (
    <Paper variant="outlined">
      <Stack padding={2} spacing={1}>
        <Box>
          <Typography variant="overline">{props.title}</Typography>
        </Box>
        <Box>{props.children}</Box>
      </Stack>
    </Paper>
  );
};

export const ColoredMaterialDetails: FC<ColoredMaterialDetailsProps> = ({
  material,
  isInternal,
  grade,
  isInternalOnly,
  isRestrcited,
  isTrial,
  isInternalIndustry,
}) => {
  const { settings } = useContext<SettingsContextValue>(SettingsContext);
  const cdcUser: CdcUserAttributes = getDataFromLocalstorageByKey("cdcUser");
  const { t } = useTranslation();
  // const inactiveCategories = Object.values(settings).filter(f => !f).length;

  const isEuropean = useMemo(() => {
    if (cdcUser?.country) {
      return EuropeanCountry.some((i) => i.code === cdcUser.country);
    }
    return false;
  }, [cdcUser]);

  const isGB = useMemo(() => {
    if (cdcUser?.country) {
      return cdcUser.country === "GB";
    }
    return false;
  }, [cdcUser]);

  return (
    <Stack rowGap={2} padding={3}>
      <RestrictionsBanner value={material.restrictions ?? []} />
      {isInternalIndustry ? (
        <Alert severity="warning">
          <Typography fontWeight={700}>
            {t("retrictions_for_this_color")}
          </Typography>
          {isInternalOnly ? (
            <>
              <br />
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("internal_color_grade_combination_title")}
              </Typography>
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("internal_color_grade_combination_text")}
              </Typography>
            </>
          ) : (
            ""
          )}
          {isRestrcited ? (
            <>
              <br />
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("retricted_use_title")}
              </Typography>
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("retricted_use_text")}
              </Typography>
            </>
          ) : (
            ""
          )}
          {isTrial ? (
            <>
              <br />
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("trial_product_title")}
              </Typography>
              <Typography
                sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
              >
                {t("trial_product_text")}
              </Typography>
            </>
          ) : (
            ""
          )}
        </Alert>
      ) : (
        ""
      )}
      <Grid container columns={{ xs: 1, md: 2 }} spacing={2}>
        <Grid size={1}>
          <Stack rowGap={2}>
            {settings.colorMaterialVisible && (
              <Section title="Color & Material">
                <ColorAndMaterialInformation
                  isInternal={isInternal}
                  material={material}
                  t={t}
                />
              </Section>
            )}
            {settings.customerOemVisible && (
              <Section title="Customer / OEM">
                <CustomerInformation
                  material={material}
                  isInternal={isInternal}
                  grade={grade}
                />
              </Section>
            )}
            {settings.competitorVisible && isInternal && (
              <Section title="Competitor">
                <CompetitorInformation material={material} />
              </Section>
            )}
          </Stack>
        </Grid>
        <Grid size={1}>
          <Stack rowGap={2}>
            {settings.availabilityVisible && (
              <Section title="Availability">
                <AvailabilityInformation
                  isInternal={isInternal}
                  material={material}
                />
              </Section>
            )}
            {settings.colorSpecificationsVisible &&
              isInternal &&
              material.color.specifications && (
                <Section title="Color Specifications">
                  <ColorSpecificationList material={material} />
                </Section>
              )}
            {(isInternal || isEuropean || isGB) && (
              <Section title="Hazard Classes / Hazard Categories">
                <HazardClass sapNumber={material.availability.productNumber} />
              </Section>
            )}
            {settings.opticalMaterialDataVisible && (
              <Section title="Optical Material Data">
                <OpticalMaterialData
                  isInternal={isInternal}
                  material={material}
                />
              </Section>
            )}
            {settings.digitalSampleVisible && (
              <Box>
                <DigitalSamples material={material} isInternal={isInternal} />
              </Box>
            )}
            {/* {inactiveCategories === 1 && isInternal && <Box><Typography color="text.secondary" fontSize={11}>
            {inactiveCategories} additional category is not visible
          </Typography></Box>} */}
            {/* {inactiveCategories > 1 && isInternal && <Box><Typography color="text.secondary" fontSize={11}>
            {inactiveCategories} additional categories are not visible
          </Typography></Box>} */}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};
